<template>
  <div class="cart-item">
    <v-row v-if="!edit">
      <div class="card-logo">
        <img
          :alt="cardImageAlt"
          :src="cartItem.image"
          class="img-responsive image"
        >
      </div>
      <v-col
        :cols="editable ? 8 : 12"
        :sm="editable ? 5 : 9"
        class="text-left"
      >
        <v-row class="item-control-row">
          <v-col
            :cols="displayFulfillmentStatus ? 7 : 12"
            class="item-control-line-item-subdivider"
          >
            <div
              class="name"
              tabindex="0"
            >
              <span class="item-control-cart-name svp-font-weight-bold">{{
                cartItem.name
              }}</span>
            </div>
            <div
              class="formattedAmount"
              tabindex="0"
            >
              {{ $money(cartItem.selectedAmount, cartItem.currencyCode) }}
            </div>
            <div
              v-if="cartItem.fee && cartItem.fee.cost"
              class="cartFee"
            >
              <div
                class="formattedFee"
                v-text="formatFee(cartItem)"
              />
            </div>
            <display-if-catalog-has-physical-items>
              <div
                v-if="cartItem.fulfillmentType && !cartItemIsCashPayment"
                class="fulfillment-type"
                v-text="
                  $itemFulfillmentMixin_fulfillmentTypeLabelMap[
                    cartItem.fulfillmentType
                  ]
                "
              />
            </display-if-catalog-has-physical-items>
          </v-col>
          <v-col
            v-if="displayFulfillmentStatus"
            cols="5"
            class="item-control-fulfillment-status-subcolumn px-0 px-sm-4"
          >
            <item-control-fulfillment-status
              :fulfillment-status="cartItem.fulfillmentStatus"
            />
          </v-col>
        </v-row>
      </v-col>
      <v-col
        v-if="editable"
        cols="3"
        sm="5"
        class="controls text-right pl-0 pr-1 px-sm-2"
      >
        <tc-v-btn
          class="edit"
          tabindex="0"
          color="secondary"
          outlined
          large
          @click="toggleEdit"
          @keyup.enter.prevent="toggleEdit"
        >
          <span
            class="d-none d-sm-inline-block checkout-edit"
            aria-label="Edit amount."
          >
            {{ $t('checkout-edit') }}
          </span>
          <v-icon class="d-inline-block d-sm-none">
            mdi-pencil
          </v-icon>
        </tc-v-btn>
        <tc-v-btn
          class="remove"
          aria-label="Remove."
          tabindex="0"
          color="secondary"
          outlined
          large
          @click="removeFromCart(cartItem)"
          @keyup.enter.prevent="removeFromCart(cartItem)"
        >
          <span class="d-none d-sm-inline-block">
            {{ $t('checkout-remove') }}
          </span>
          <v-icon class="d-inline-block d-sm-none">
            mdi-close
          </v-icon>
        </tc-v-btn>
      </v-col>
      <v-col
        v-if="showOrderReferenceDetail"
        class="text-left py-0"
      >
        <order-reference-detail
          class="mb-0"
          :reference-id="orderRefId"
        />
      </v-col>
    </v-row>
    <div
      v-else
      id="checkout-update-panel"
      :class="{ 'is-editing': edit }"
    >
      <update-panel
        :cart-item="cartItem"
        @finish-checkout-edit="toggleEdit"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { REMOVE_CART_ITEM } from '@/store/action-types'
import { GET_CART_EXTERNAL_REF_ID } from '@/store/get-types'
import ItemControlFulfillmentStatus from '@/components/cart/ItemControlFulfillmentStatus'
import ItemUpdatePanel from '@/components/cart/ItemUpdatePanel'
import OrderReferenceDetail from '@/components/history/OrderReferenceDetail'
import { ItemFeeMixin } from '@/components/shared/mixins/item-fee-mixin'
import { ItemFulfillmentMixin } from '@/components/shared/mixins/item-fulfillment-mixin'
import DisplayIfCatalogHasPhysicalItems from '@/components/shared/catalog/DisplayIfCatalogHasPhysicalItems'
import CartItem from '@/store/classes/cart-item'
import { CHECKOUT_ITEM_EDIT } from '@/plugins/segment/SegmentConstants'
import { PAYMENT_REWARD_TYPES } from '@/components/shared/item/reward-type-constants'

export default {
  name: 'ItemControl',
  components: {
    OrderReferenceDetail,
    DisplayIfCatalogHasPhysicalItems,
    ItemControlFulfillmentStatus,
    'update-panel': ItemUpdatePanel
  },
  mixins: [ItemFeeMixin, ItemFulfillmentMixin],
  props: {
    cartItem: {
      type: [CartItem, Object],
      required: true
    },
    editable: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      edit: false
    }
  },
  computed: {
    ...mapGetters({
      cartExternalRefId: GET_CART_EXTERNAL_REF_ID
    }),
    cartItemIsCashPayment () {
      const catalogItem = this.cartItem.brand.items.find(item => item.utid === this.cartItem.utid)

      return catalogItem && PAYMENT_REWARD_TYPES.includes(catalogItem.rewardType.description)
    },
    displayFulfillmentStatus () {
      return this.cartItem.fulfillmentStatus
    },
    showOrderReferenceDetail () {
      return !!this.cartItem.lineNumber
    },
    orderRefId () {
      const { lineNumber } = this.cartItem
      return `${this.cartExternalRefId}-${lineNumber}`
    },
    cardImageAlt () {
      const { brand } = this.cartItem || {}
      const { displayName = '' } = brand || {}
      return this.$t('checkout-item-control-card-image', { displayName }).trim()
    }
  },
  methods: {
    ...mapActions({
      removeFromCart: REMOVE_CART_ITEM
    }),
    toggleEdit () {
      this.edit = !this.edit

      if (this.edit) {
        this.$segment.track(CHECKOUT_ITEM_EDIT)
      }
    }
  }
}
</script>

<style lang="scss">
@import '~@/assets/css/variables';
@import '~@/scss/variables';

.name {
  font-size: $font-size-md;
  color: $tangoTextPrimary;
}
.formattedAmount {
  color: $tangoTextPrimary;
}
.fulfillment-type {
  color: $tangoTextPrimary;
}
.text-left {
  font-size: $font-size-md;
}
.remove {
  color: $tangoTextPrimary;
}
.checkout-edit {
  font-size: $font-size-md;
}
.text-left {
  font-size: $font-size-md;
}
#checkout-update-panel {
  width: 100%;
  .card-logo {
    .img-responsive {
      width: 100%;
    }
  }
}
.card-logo {
  width: 25%;
  padding: 0 15px;

  .img-responsive {
    width: 100%;
  }
}

.shopping-cart {
  .no-padding {
    padding: 0;
  }
}

.cart-item .controls {
  .v-btn {
    // 80px to match the width needed for the Update button
    // We don't want the button widths to jump when clicking on them
    min-width: 80px;
    padding-left: 0;
    padding-right: 0;
    &.edit {
      margin-bottom: map-get($spacers, 1);
      @media #{map-get($display-breakpoints, 'sm-and-up')} {
        margin-bottom: 0;
        margin-right: map-get($spacers, 2);
      }
    }
  }
}

.item-control-fulfillment-status-subcolumn {
  display: flex;
  justify-content: center;
}

@media screen and (min-width: $breakpoint-md) {
  .controls {
    position: absolute;
    right: 0;
    top: 9px;
  }
}

@media screen and (max-width: $breakpoint-sm - 1) {
  .card-logo {
    display: none;
  }
}
</style>
